import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

/** Commands the ⌘K palette offers. Screens register their own (e.g. "Hold sale" while on
 * Checkout) with useRegisterCommands; the shell registers navigation and account actions. */
export type Command = {
  id: string;
  label: string;
  group: string;
  icon?: ReactNode;
  keywords?: string;
  shortcut?: string;
  run: () => void;
};

type Registry = {
  commands: Command[];
  register: (owner: symbol, cmds: Command[]) => void;
  unregister: (owner: symbol) => void;
};
const Ctx = createContext<Registry | null>(null);

export function CommandProvider({ children }: { children: ReactNode }) {
  const [map, setMap] = useState<Map<symbol, Command[]>>(new Map());
  // register/unregister must keep one identity: registering changes `map`, and if that gave
  // them new identities, every registrant's effect would re-run and register again, forever.
  const register = useCallback(
    (owner: symbol, cmds: Command[]) =>
      setMap((m) => new Map(m).set(owner, cmds)),
    [],
  );
  const unregister = useCallback(
    (owner: symbol) =>
      setMap((m) => {
        if (!m.has(owner)) return m;
        const n = new Map(m);
        n.delete(owner);
        return n;
      }),
    [],
  );
  const value = useMemo<Registry>(
    () => ({ commands: Array.from(map.values()).flat(), register, unregister }),
    [map, register, unregister],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCommands(): Command[] {
  return useContext(Ctx)?.commands ?? [];
}

/** Register commands for as long as the calling component is mounted. `cmds` should be
 * memoised by the caller; a new array re-registers. */
export function useRegisterCommands(cmds: Command[]): void {
  const reg = useContext(Ctx);
  const owner = useRef(Symbol("commands"));
  const register = reg?.register;
  const unregister = reg?.unregister;
  useEffect(() => {
    if (!register || !unregister) return;
    const me = owner.current;
    register(me, cmds);
    return () => unregister(me);
  }, [cmds, register, unregister]);
}

/** Subsequence match with a score: contiguous and word-start hits rank higher. */
export function score(query: string, text: string): number {
  const q = query.trim().toLowerCase();
  if (!q) return 1;
  const t = text.toLowerCase();
  const idx = t.indexOf(q);
  if (idx === 0) return 100;
  if (idx > 0)
    return (/\s/.test(t[idx - 1] ?? "") ? 80 : 60) - Math.min(idx, 20);
  let ti = 0;
  let s = 0;
  for (const ch of q) {
    const f = t.indexOf(ch, ti);
    if (f === -1) return 0;
    s += f === ti ? 3 : 1;
    ti = f + 1;
  }
  return s;
}
